import classNames from 'classnames';
import { ScommesseEsitoSkeleton } from 'components/scommesseEsitoSkeleton';
import { ScommesseTableSubtitle } from 'components/scommesseTable/scommesseTableSubtitle';
import { useSnaiRunnerContext } from 'context/SnaiRunner';
import { useCartToggleHandler, useIsInCart } from 'features/carrello/hooks';
import { KeyManagerSport } from 'features/sport';
import { snaiRunnerToCartHandler } from 'features/sport/components/sportTicket/sportTicketActions';
import { selectIsSnaiRunnerInCart } from 'features/sport/components/sportTicket/sportTicketSelectors';
import {
  useEsito,
  useInfoEsito,
  useIsAvvenimento,
  useIsHeaderVisible,
  useIsInfoAggActive,
  useIsInfoAggActiveByScommessaKey,
  useIsMarcatoriPage,
} from 'features/sport/hooks';
import { useQuotaVariation } from 'hooks';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { memo, useMemo } from 'react';
import { ItemWithTranslation } from 'types/swagger';
import { isNotEmpty, isTruthy } from 'utility/functions';
import styles from './ScommesseMarcatoriEsitoTipoBox.module.scss';

export type ScommesseMarcatoriEsitoTipoBoxProps = ItemWithTranslation<{ header?: string }> & {
  keyEsito: string;
  containerClassName?: string;
  classNameButton?: string;
  statePath?: string[];
  hasHeader?: boolean;
  showHeader?: boolean;
  showHeaderOutside?: boolean;
  gtagAction?: () => void;
};

export const ScommesseMarcatoriEsitoTipoBox = memo(function ScommesseMarcatoriEsitoTipoBox({
  header,
  showHeader,
  showHeaderOutside,
  keyEsito,
  headerTrKey,
  classNameButton,
  containerClassName,
  gtagAction,
}: ScommesseMarcatoriEsitoTipoBoxProps) {
  const dispatch = useAppDispatch();
  const isActiveInCart = useIsInCart(keyEsito);
  const isSnaiRunnerInCart = useTypedSelector((st) => selectIsSnaiRunnerInCart(st, keyEsito));
  const isHeaderVisible = useIsHeaderVisible();
  const isAvvenimento = useIsAvvenimento();
  const isMarcatorePage = useIsMarcatoriPage();

  const km = useMemo(() => new KeyManagerSport(keyEsito), [keyEsito]);
  const maybeEsito = useEsito(keyEsito);
  const { quota, isActive: isEsitoActive, infoEsitoKey } = maybeEsito ?? {};

  const isSnaiRunner = useSnaiRunnerContext();
  const maybeInfoEsito = useInfoEsito(infoEsitoKey);

  const onCartToggleHandler = useCartToggleHandler(keyEsito);

  const { useIsActiveHook, isActiveKey } = useMemo(() => {
    const { scommessaKey } = km;
    // N.B. splitInfoAggiuntiva should come from infoEsito only, but temporarly we allow esito source for backward compatibility
    const { splitInfoAggiuntiva } = { ...maybeInfoEsito, ...maybeEsito };

    if (splitInfoAggiuntiva) {
      return {
        isActiveKey: `${scommessaKey}_${splitInfoAggiuntiva}`,
        useIsActiveHook: useIsInfoAggActive,
      };
    }

    return {
      isActiveKey: scommessaKey,
      useIsActiveHook: useIsInfoAggActiveByScommessaKey,
    };
  }, [km, maybeEsito, maybeInfoEsito]);

  const isInfoAggActive = useIsActiveHook(isActiveKey);

  const [isArrowVisible, isIncreasing, isDecreasing] = useQuotaVariation(maybeEsito);

  const quotaValue = useMemo(
    () => (isEsitoActive && isInfoAggActive ? (quota! < 10 ? quota!.toFixed(2) : quota) : '-'),
    [isEsitoActive, isInfoAggActive, quota]
  );

  if (!maybeEsito) {
    return <ScommesseEsitoSkeleton header={header} testId={keyEsito} className={styles.skeleton} />;
  }

  return (
    <div
      className={classNames(styles.boxContainer, containerClassName, {
        [styles.fullWidth]: isAvvenimento && isMarcatorePage,
      })}
      data-key={'sport-skeleton'}
    >
      {isTruthy(showHeaderOutside) && isNotEmpty(header) && (
        <ScommesseTableSubtitle
          header={header}
          headerTrKey={headerTrKey}
          className={classNames(styles.subTitle, {
            [styles.activeSubTitle]: isSnaiRunnerInCart || isActiveInCart,
            [styles.headerVisible]: isNotEmpty(header) && isHeaderVisible && isAvvenimento,
          })}
        />
      )}
      <button
        type="button"
        disabled={!isEsitoActive || !isInfoAggActive}
        className={classNames(styles.container, classNameButton, {
          [styles.active]: isSnaiRunner ? isSnaiRunnerInCart : isActiveInCart,
        })}
        onClick={() => {
          gtagAction && gtagAction();
          !isSnaiRunner ? onCartToggleHandler() : dispatch(snaiRunnerToCartHandler(keyEsito));
        }}
      >
        {!showHeaderOutside && isTruthy(showHeader) && isNotEmpty(header) && isHeaderVisible && (
          <ScommesseTableSubtitle
            header={header}
            headerTrKey={headerTrKey}
            className={classNames({
              [styles.activeSubTitle]: isSnaiRunner ? isSnaiRunnerInCart : isActiveInCart,
            })}
          />
        )}
        <span
          className={classNames(styles.quotaWrapper, {
            [styles.changed]: isArrowVisible,
            [styles.increasing]: isIncreasing,
            [styles.decreasing]: isDecreasing,
          })}
        >
          {quotaValue}
        </span>
      </button>
    </div>
  );
});
